import React, { useEffect, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  InputNumber,
  Popover,
  Row,
  Spin,
  Statistic,
  Tag,
  Tooltip,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  ClockCircleOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import ProgressGraph from "./ProgressGraph";
import moment from "moment";
import { getTargetString } from "../../helpers/targetHelpers";
import UpdateTargetApi from "../../api/target-progress/UpdateTargetApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import TargetProgressHistoryApi from "../../api/target-progress/TargetProgressHistoryApi";
import ProgressHistoryTable from "../tables/ProgressHistoryTable";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { permissionAbility } from "../../helpers/permissionHelpers";
import UpdateTargetProgressModal from "../modal/UpdateTargetProgressModal";

function PlanOverview(props) {
  const [submit, setSubmit] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState();
  const [historyLoading, setHistoryLoading] = useState(false);
  const [targetProgress, setTargetProgress] = useState(0);
  const [update, setUpdate] = useState(false);
  const [canUpdate, setCanUpdate] = useState(false);
  const [hasPendingTargetProgress, setHasPendingTargetProgress] = useState(false);
  const positionState = useSelector((state) => state.positionState.value);
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const isCycleCompleted = perfCycleState?.status === "Completed";
  const userLevel = positionState?.userRole?.name;
  const [targetAchived, setTargetAchieved] = useState(0);
  const [performance, setPerformance] = useState(0);
  let curLocation = useLocation();

  useEffect(() => {
    setTargetProgress(props.data?.targetAchieved || 0);
    setPerformance(props.data?.performance || 0);
    setHasPendingTargetProgress(props.data?.hasPendingTargetProgress || false);
    checkCanUpdate();
  }, [curLocation]);

  useEffect(() => {
    if (targetAchived) {
      setPerformance(((targetAchived / props.data?.target) * 100).toFixed(2));
    }
  }, [targetAchived]);

  function checkCanUpdate() {
    if (positionState?.id === props.data?.assignedTo?.positionId) {
      setCanUpdate(true);
    } else {
      setCanUpdate(false);
    }
  }

  async function onFinish(values) {
    setSubmit(true);
    const apiResponse = await UpdateTargetApi(
      props.data?.planId,
      values.target,
      positionState?.id
    );
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "success") {
      if (apiResponse.targetProgress?.approvalStatus === "Approved") {
        openNotificationSuccess("bottomRight", `Target updated`);
        setTargetProgress(values.target);
        setTargetAchieved(values.target);
        setHasPendingTargetProgress(false);
        props.updateTarget(values.target);
        // Refresh parent data to update hasPendingTargetProgress status
        if (props.onDataUpdate) {
          props.onDataUpdate();
        }
      } else {
        // Immediately set pending status to true for instant visual feedback
        setHasPendingTargetProgress(true);
        openNotificationSuccess(
          "bottomRight",
          `Target updated but approval is pending`
        );
      }
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't update target. Try again later."
      );
    }
    setSubmit(false);
    setUpdate(false);
  }

  async function getProgressHistory() {
    setHistoryLoading(true);
    const apiResponse = await TargetProgressHistoryApi(props.data?.planId);
    if (apiResponse.message === "success") {
      setHistory(apiResponse);
      setHistoryLoading(false);
    }
  }

  function expandHistory() {
    setShowHistory(!showHistory);
    if (!history) {
      getProgressHistory();
    }
  }

  function renderAttributes() {
    return props.data?.planAttributeValue
      ?.sort((a, b) => a.name.localeCompare(b.name))
      .filter((attribute) => {
        // Filter out empty attributes
        if (attribute.valueType === "text") {
          return attribute.text && attribute.text !== "" && attribute.text !== "-";
        } else if (attribute.valueType === "number") {
          return attribute.number !== null && attribute.number !== undefined && attribute.number !== "";
        } else if (attribute.valueType === "double") {
          return attribute.double !== null && attribute.double !== undefined && attribute.double !== "";
        } else if (attribute.valueType === "dropdown") {
          // Check if dropdown has actual values (not empty, not "-", not just IDs that resolve to empty)
          if (!attribute.dropdown || attribute.dropdown === "" || attribute.dropdown === "-") {
            return false;
          }
          // Also check if the resolved value is meaningful
          if (attribute.value && attribute.value !== "-" && attribute.value !== "") {
            return true;
          }
          return false;
        } else if (attribute.valueType === "date") {
          return attribute.date && attribute.date !== "" && attribute.date !== "-";
        } else if (attribute.valueType === "boolean") {
          return attribute.value !== null && attribute.value !== undefined && attribute.value !== "";
        }
        return false;
      })
      .map((attribute) => {
        let displayValue = "-";
        
        if (attribute.valueType === "text") {
          displayValue = attribute.text || "-";
        } else if (attribute.valueType === "number") {
          displayValue = attribute.number || "-";
        } else if (attribute.valueType === "double") {
          displayValue = attribute.double || "-";
        } else if (attribute.valueType === "dropdown") {
          // First, try to use the resolved display value from backend if it looks like resolved names
          if (attribute.value && typeof attribute.value === 'string' && 
              !attribute.value.match(/^\d+(,\d+)*$/)) { // Not just comma-separated numbers
            displayValue = attribute.value;
          } else if (attribute.dropdownOptions && attribute.dropdown) {
            // Resolve IDs to names manually
            try {
              // Handle comma-separated format: "11111,22222,33333"
              let idArray;
              if (attribute.dropdown.includes(',')) {
                idArray = attribute.dropdown.split(',').map(id => id.trim());
              } else {
                // Single value
                idArray = [attribute.dropdown.trim()];
              }
              
              const values = idArray.map(id => {
                const option = attribute.dropdownOptions.find(opt => String(opt.id) === String(id));
                return option ? option.value : id;
              });
              displayValue = values.join(", ");
            } catch {
              displayValue = attribute.value || "-";
            }
          } else {
            displayValue = attribute.value || "-";
          }
        } else if (attribute.valueType === "date") {
          displayValue = attribute.date ? moment(attribute.date).format("DD.MM.YYYY") : "-";
        } else if (attribute.valueType === "boolean") {
          displayValue = attribute.value === true ? "Yes" : attribute.value === false ? "No" : "-";
        }
        
        return (
          <Descriptions.Item
            key={attribute.id}
            label={attribute.name}
            className="w-1/2"
          >
            {displayValue}
          </Descriptions.Item>
        );
      });
  }

  return (
    <div className="pb-8">
      <div className="mb-4">
        <div className="text-lg mb-1">{props.data?.title}</div>{" "}
        <div className="flex items-center mb-1">
          Unit:&nbsp;
          <div
            className="w-2 h-2 mr-1 rounded-full"
            style={{ backgroundColor: props.data?.unit?.orgLevelColor }}
          />
          <div>{props.data?.unit?.name}</div>
          <Popover
            placement="top"
            content={
              <div className="text-xs">
                {props.data?.unit?.name}
                {props.data?.unit?.headUnit &&
                  " < " + props.data?.unit?.headUnit?.name}
                {props.data?.unit?.headUnit?.headUnit &&
                  " < " + props.data?.unit?.headUnit?.headUnit?.name}
              </div>
            }
            trigger="hover"
          >
            <div className="text-gray-500 text-xs ml-1">
              <InfoCircleOutlined />
            </div>
          </Popover>
        </div>
        <div className="flex items-center mb-1">
          User:&nbsp;
          {props.data?.assignedTo?.imageUrl ? (
            <Avatar
              className="cursor-pointer"
              src={props.data?.assignedTo?.imageUrl}
            />
          ) : (
            <Avatar>
              {props.data?.assignedTo?.firstName ? (
                props.data?.assignedTo?.firstName?.charAt(0).toUpperCase()
              ) : (
                <UserOutlined />
              )}
            </Avatar>
          )}
          <div className="ml-2">
            <div>
              {props.data?.assignedTo?.firstName
                ? props.data?.assignedTo?.firstName +
                  " " +
                  props.data?.assignedTo?.lastName
                : "-"}
            </div>
            <div className="text-xs -mt-1">
              {props.data?.assignedTo?.designationAbb}
            </div>
          </div>
        </div>
        {props.data?.planGroup && (
          <div className="flex items-center mb-1 gap-1">
            Plan group: <Tag>{props.data?.planGroup}</Tag>
          </div>
        )}
        {props.data?.targetDate && (
          <div className="flex items-center mb-1 gap-1">
            Target date:
            <div className="text-xs text-gray-400">
              {props.data?.targetDate
                ? moment(props.data?.targetDate).format("DD.MM.YYYY")
                : ""}
            </div>
          </div>
        )}
        {props.data?.description && (
          <div className="max-w-xl">Description: {props.data?.description}</div>
        )}
      </div>
      {props.data?.target !== 0 && props.data?.target && (
        <>
          <div className="mb-4">
            <Statistic
              title="CURRENT PROGRESS"
              value={performance || 0}
              prefix={
                props.data?.progressStatus === "On Track" ? (
                  <ArrowUpOutlined style={{ color: "green" }} />
                ) : props.data?.progressStatus === "Off Track" ? (
                  <ArrowDownOutlined style={{ color: "orange" }} />
                ) : (
                  <ArrowDownOutlined style={{ color: "red" }} />
                )
              }
              suffix="% completed"
            />
          </div>

          <div className="mb-6 max-w-3xl">
            <div className="border rounded">
              <div
                className={
                  "p-3 flex justify-between items-center " +
                  (showHistory && "border-b")
                }
              >
                <div className="flex items-center">
                  <div>
                    <div className="">Target</div>
                    <div className="flex items-center">
                      <div className="text-lg">
                        {targetProgress
                          ? getTargetString(
                              targetProgress,
                              props.data?.scale?.scaleName,
                              props.data?.scale?.scaleUnitAbbreviation
                            )
                          : "0"}
                      </div>
                      <div className="mx-1 text-lg">/</div>
                      <div className="text-lg">
                        {getTargetString(
                          props.data?.target,
                          props.data?.scale?.scaleName,
                          props.data?.scale?.scaleUnitAbbreviation
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  {isCycleCompleted ? (
                    <>
                      <Popover
                        placement="top"
                        content={
                          <div className="text-xs">
                            Performance cycle is completed
                          </div>
                        }
                        trigger="hover"
                      >
                        <div className="text-gray-500 text-xs mr-1">
                          <InfoCircleOutlined />
                        </div>
                      </Popover>
                      <Button disabled type="primary">
                        Update
                      </Button>
                    </>
                  ) : canUpdate ? (
                    hasPendingTargetProgress ? (
                      <>
                        <Popover
                          placement="top"
                          content={
                            <div className="text-xs">
                              Target update pending approval
                            </div>
                          }
                          trigger="hover"
                        >
                          <div className="text-gray-500 text-xs mr-1">
                            <InfoCircleOutlined />
                          </div>
                        </Popover>
                        <Button disabled type="primary">
                          Update
                        </Button>
                      </>
                    ) : (
                      <Button onClick={() => setUpdate(true)} type="primary">
                        Update
                      </Button>
                    )
                  ) : (
                    <>
                      <Popover
                        placement="top"
                        content={
                          <div className="text-xs">
                            You can't update this target
                          </div>
                        }
                        trigger="hover"
                      >
                        <div className="text-gray-500 text-xs mr-1">
                          <InfoCircleOutlined />
                        </div>
                      </Popover>
                      <Button disabled type="primary">
                        Update
                      </Button>
                    </>
                  )}
                  {permissionAbility(userLevel, "plans", "edit") && (
                    <Tooltip title={"Update History"}>
                      <div
                        className="cursor-pointer text-blue-500 hover:text-blue-600 ml-2"
                        onClick={() => expandHistory()}
                      >
                        <HistoryOutlined />
                      </div>
                    </Tooltip>
                  )}
                </div>
              </div>
              {showHistory && (
                <div className="max-h-64 overflow-auto custom-scroll">
                  <div className="p-3">
                    <ProgressHistoryTable
                      data={history?.targetProgressHistory}
                      handleOk={() => getProgressHistory()}
                      loading={historyLoading}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      <div className="mb-6 max-w-3xl">
        <Descriptions column={1} bordered>
          {renderAttributes()}
        </Descriptions>
      </div>
      {update && (
        <UpdateTargetProgressModal
          visible={update}
          handleCancel={() => setUpdate(false)}
          data={props.data}
          targetProgress={targetProgress}
          onFinish={onFinish}
          submit={submit}
        />
      )}
    </div>
  );
}

export default PlanOverview;
